/**
 * loadError — tell APART "nothing here" from "we could not look".
 * Card P114 PR-3.
 *
 * ── ⛔⛔ THE STATE THIS FIXES ───────────────────────────────────────────────
 *
 * Measured during the P108 staff-feedback triage (24 Oct 2026). Seven panels
 * on the Stripe reports page shared this shape:
 *
 *     try { const r = await api.stripeReportX(); if (r?.success) setX(r.data); }
 *     catch {}
 *
 * An EMPTY `catch`. A 401 from a wrong key, a 500, a network drop and a
 * genuinely quiet month all produced the same screen: a grey panel reading
 * "No payment data available". Staff reported those as dead clicks, and there
 * was no way — from the screen or from the console — to tell which had
 * happened. The Logistics clinic dropdown had the same defect in miniature
 * (`.catch(() => [])`), which is why "All Clinics cannot select" was filed as
 * a UI bug when the likely cause is an empty table or a refusal.
 *
 * Three states, not two: DATA / EMPTY / FAILED. A control that cannot say
 * "I could not look" will eventually be believed when it says "there is
 * nothing here".
 *
 * ── ⛔⛔ WHY THE MESSAGE IS BUILT, NOT FORWARDED ────────────────────────────
 *
 * The obvious fix is `setError(err.message)`. We do not do that, and the
 * reason is PHI. A payments provider's message is not a controlled string:
 * Stripe emits `No such customer: cus_…`, and a failing query can echo a
 * parameter back. Rendering a provider's prose onto a billing screen — and
 * writing it to the browser console, where it reaches logs and screenshots —
 * is an uncontrolled disclosure path for exactly the kind of record this page
 * is about.
 *
 * So `describeLoadError` is an ALLOWLIST, not a sanitiser: it reads only a
 * numeric status and a short machine code matching /^[A-Za-z0-9_.-]{1,40}$/,
 * and throws everything else away. There is no branch that can return
 * attacker- or provider-controlled prose, which is a stronger claim than
 * "we strip the bad parts".
 */
"use strict";

/** Machine codes only: letters, digits, underscore, dot, dash. Never prose. */
const SAFE_CODE = /^[A-Za-z0-9_.-]{1,40}$/;

/** HTTP-ish status, if one is present in any of the shapes callers see. */
function readStatus(err) {
  if (!err || typeof err !== "object") return null;
  for (const v of [err.status, err.statusCode, err.code, err.response && err.response.status]) {
    const n = Number(v);
    if (Number.isInteger(n) && n >= 100 && n <= 599) return n;
  }
  return null;
}

/**
 * A short machine code (Stripe `type`/`code`, our own `code`), if safe.
 *
 * ⚠ `err.name` IS DELIBERATELY NOT READ, and a red arm is why. It was, in the
 * first draft — and because every JS Error carries `name`, a plain
 * `new Error(...)` always produced the code "Error". Two consequences, both
 * bad: the reader was told "Couldn't load X — Error.", which says nothing;
 * and the final `connection problem` fallback became unreachable for the
 * commonest failure there is, so a planted leak in that branch could not be
 * caught by any test. Dropping `name` makes an ordinary network failure read
 * as "connection problem" AND makes that branch reachable, which is what
 * lets it be guarded.
 */
function readCode(err) {
  if (!err || typeof err !== "object") return null;
  for (const v of [err.code, err.type]) {
    if (typeof v === "string" && SAFE_CODE.test(v) && !/^\d+$/.test(v)) return v;
  }
  return null;
}

/**
 * Describe a failed load in terms safe to show AND safe to log.
 *
 * @param {*} err  anything a rejected promise or a `{success:false}` payload
 *                 handed us — never trusted, never echoed.
 * @returns {{status: number|null, code: string|null, reason: string}}
 *          `reason` is a SHORT phrase assembled here from the two allowlisted
 *          fields. It never contains provider or user text.
 */
export function describeLoadError(err) {
  const status = readStatus(err);
  const code = readCode(err);

  let reason;
  if (status === 401 || status === 403) reason = "not authorised";
  else if (status === 404) reason = "not found";
  else if (status === 429) reason = "rate limited";
  else if (status != null && status >= 500) reason = `server error ${status}`;
  else if (status != null) reason = `request failed (${status})`;
  else if (code) reason = code;
  else reason = "connection problem";

  return { status, code, reason };
}

/**
 * The one-line sentence a panel shows and the console records.
 * @param {string} what  what could not be loaded, e.g. "the payment report"
 */
export function loadErrorText(what, err) {
  return `Couldn't load ${what} — ${describeLoadError(err).reason}.`;
}

/**
 * Log a failed load without putting anything uncontrolled in the console.
 *
 * ⚠ The raw error is NOT passed to console. A browser console is read over
 * shoulders, pasted into tickets and captured by screenshots; it is not a
 * private channel, and this page is about payments.
 */
export function logLoadError(what, err) {
  const { status, code, reason } = describeLoadError(err);
  // eslint-disable-next-line no-console
  console.error(`[load] ${what} failed: ${reason}`, { status, code });
}
